import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { publicRsvp } from "../../server/Api";

const normalizeChoice = (value) => {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (raw === "y" || raw === "yes") return "yes";
  if (raw === "n" || raw === "no") return "no";
  return "";
};

const GuestRsvp = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Recording your RSVP…");

  const params = useMemo(() => {
    const token = searchParams.get("t") || searchParams.get("token") || "";
    const response = normalizeChoice(
      searchParams.get("r") || searchParams.get("response")
    );
    return {
      token,
      response,
      // Legacy long-form links (already sent emails)
      eventId: searchParams.get("eventId") || "",
      regNo: searchParams.get("regNo") || "",
      occasionIds: searchParams.get("occasionIds") || "",
    };
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    const submit = async () => {
      const hasToken = Boolean(params.token);
      const hasLegacy =
        params.eventId && params.regNo && params.occasionIds;
      const hasResponse =
        params.response === "yes" || params.response === "no";

      if (!hasResponse || (!hasToken && !hasLegacy)) {
        if (!cancelled) {
          setStatus("error");
          setMessage(
            "This RSVP link is incomplete. Please use the Yes/No buttons from your invitation email."
          );
        }
        return;
      }

      try {
        const query = hasToken
          ? { t: params.token, r: params.response === "yes" ? "y" : "n" }
          : {
              eventId: params.eventId,
              regNo: params.regNo,
              occasionIds: params.occasionIds,
              response: params.response,
            };

        await axios.get(publicRsvp(), { params: query });
        if (cancelled) return;
        setStatus("success");
        setMessage(
          params.response === "yes"
            ? "Thank you — your RSVP of Yes has been recorded."
            : "Thank you — your RSVP of No has been recorded."
        );
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(
          err?.response?.data?.message ||
            "We could not record your RSVP. Please contact the event host."
        );
      }
    };

    submit();
    return () => {
      cancelled = true;
    };
  }, [params]);

  return (
    <div className="guest-rsvp-page">
      <div className="guest-rsvp-card">
        <p className="guest-rsvp-brand">Festiva</p>
        <h1>
          {status === "loading"
            ? "Confirming RSVP"
            : status === "success"
              ? "RSVP received"
              : "Unable to confirm"}
        </h1>
        <p className="guest-rsvp-message">{message}</p>
        {params.response === "yes" || params.response === "no" ? (
          <p className={`guest-rsvp-badge is-${params.response}`}>
            {params.response === "yes" ? "Yes" : "No"}
          </p>
        ) : null}
      </div>
      <style>{`
        .guest-rsvp-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background:
            radial-gradient(circle at top left, rgba(0,128,128,0.12), transparent 40%),
            linear-gradient(160deg, #eef2f4 0%, #f7fafb 55%, #e8f3f3 100%);
          font-family: Georgia, "Times New Roman", Times, serif;
        }
        .guest-rsvp-card {
          width: min(440px, 100%);
          background: #fff;
          border-radius: 12px;
          padding: 36px 32px;
          text-align: center;
          box-shadow: 0 12px 40px rgba(31, 42, 48, 0.08);
          border: 1px solid #d7e6e6;
        }
        .guest-rsvp-brand {
          margin: 0 0 12px;
          font-family: Helvetica, Arial, sans-serif;
          font-size: 11px;
          letter-spacing: 3px;
          font-weight: 700;
          color: #008080;
          text-transform: uppercase;
        }
        .guest-rsvp-card h1 {
          margin: 0 0 12px;
          font-size: 28px;
          color: #1f2a30;
        }
        .guest-rsvp-message {
          margin: 0;
          font-family: Helvetica, Arial, sans-serif;
          font-size: 15px;
          line-height: 1.5;
          color: #5b646c;
        }
        .guest-rsvp-badge {
          display: inline-block;
          margin-top: 20px;
          padding: 8px 22px;
          border-radius: 6px;
          font-family: Helvetica, Arial, sans-serif;
          font-weight: 700;
          font-size: 14px;
        }
        .guest-rsvp-badge.is-yes {
          background: #008080;
          color: #fff;
        }
        .guest-rsvp-badge.is-no {
          background: #fff;
          color: #008080;
          border: 2px solid #008080;
        }
      `}</style>
    </div>
  );
};

export default GuestRsvp;
